// Shared metadata for service/standard pages, used by both the English and
// Arabic slug routes. serviceType is the specific, disambiguated service name
// search and AI answer engines map to a query. Falls back to the item title.
export const SERVICE_TYPE: Record<string, string> = {
  "iso-9001": "ISO 9001 quality management system preparation support",
  "iso-14001": "ISO 14001 Environmental Management System Certification",
  "iso-45001": "ISO 45001 Occupational Health and Safety Certification",
  "iso-22000": "ISO 22000 Food Safety Management Certification",
  "iso-29001": "ISO 29001 Petroleum and Natural Gas Quality Certification",
  "iso-27001": "ISO 27001 Information Security Management Certification",
  "ministry-of-oil-vendor-registration": "Iraqi Ministry of Oil vendor registration dossier review",
  "oil-gas-registration": "Oil and gas vendor registration dossier review",
};

// Oil & gas services explicitly cover the Kurdistan Region too.
export const KRI_SLUGS = new Set([
  "iso-29001",
  "ministry-of-oil-vendor-registration",
  "oil-gas-registration",
]);

export function areaServedFor(slug: string): Array<{ type: "Country" | "Place"; name: string }> {
  return KRI_SLUGS.has(slug)
    ? [
        { type: "Country", name: "Iraq" },
        { type: "Place", name: "Kurdistan Region of Iraq" },
      ]
    : [{ type: "Country", name: "Iraq" }];
}

/**
 * A service page's meta title is "<label>: <title>". The label only earns its
 * place when it adds something:
 *   - not when the title already names the standard ("ISO 22000: Food Safety"
 *     was rendering as "ISO 22000: ISO 22000: Food Safety"),
 *   - not when the label is an English phrase leading an Arabic title
 *     ("ISO 9001" travels between the editions, "Ministry of Iraqi Oil"
 *     does not).
 */
export function titleWithLabel(label: string, title: string, lang: "en" | "ar") {
  if (!label) return title;
  const code = label.match(/\d+/)?.[0];
  if (title.includes(label) || (code && title.includes(code))) return title;
  if (lang === "ar" && /[a-z]/.test(label)) return title;
  return `${label}: ${title}`;
}

/**
 * Meta descriptions written for the search result rather than for the page.
 * A hero subtitle is a design line and is often too short to be useful in a
 * SERP (Google wants roughly 70-160 characters); where that is the case the
 * slug is listed here. Anything not listed falls back to the hero subtitle.
 */
export const META_DESCRIPTION: Record<string, { en: string; ar: string }> = {
  "iso-27001": {
    en: "ISO 27001 information-security management-system preparation in Iraq. Review activities, sites, issuing body and buyer requirements before arranging an audit.",
    ar: "دعم التحضير لنظام إدارة أمن المعلومات ISO 27001 في العراق. راجع الأنشطة والمواقع والجهة المانحة ومتطلبات المشتري قبل ترتيب التدقيق.",
  },
  // No accreditation claim here: the site discloses that ISO 29001 sits
  // outside AQSR's UAF-accredited scope.
  "iso-29001": {
    en: "ISO 29001 preparation for petroleum and natural-gas suppliers in Iraq. Confirm the required scope, issuing body and buyer’s acceptance criteria.",
    ar: "التحضير لمواصفة ISO 29001 لموردي النفط والغاز في العراق. تحقّق من النطاق المطلوب وجهة المنح وشروط قبول المشتري.",
  },
};
